import { ServiceConfig } from 'constants/shared/ServiceConfig';
import { makeAutoObservable } from 'mobx';
import { CREATE_BILL_TEMPLATE } from 'network/shared/bill';
import { CREATE_LESSON_REPORT_TEMPLATE } from 'network/shared/lessonReport';
import {
  LessonReportItemT,
  LessonReportItemTypeT,
  LessonReportTemplateT,
} from 'types/shared/core/LessonReport';
import { MyResponse } from 'types/shared/reqAndRes/MyResponse';
import { api } from 'utils/api/api';
import interfaceBridge from 'utils/interfaceBridge';
import { MyError } from 'utils/MyError';
import { LessonReportTemplateFormValuesT } from './formData';

type ValidationResultT =
  | { isValid: true; errorMessage?: undefined }
  | { isValid: false; errorMessage: string };

class LessonReportTemplateAddStore {
  templateItems: LessonReportItemT[] = [];
  private nextNo = 1;
  // 사라지는 row
  disappearingRowAfterIndex: number | null = null;
  // 나타나는 쪽은 기존대로
  lastMovedNo: number | null = null;
  moveTick = 0;

  constructor(initial?: Partial<{ items: LessonReportItemT[] }>) {
    makeAutoObservable(this, {}, { autoBind: true });

    if (initial?.items?.length) {
      // 주어진 아이템 사용 + index 재계산
      this.templateItems = initial.items
        .map((it, idx) => ({
          ...it,
          index: idx,
        }))
        .sort((a, b) => a.index - b.index);

      this.nextNo = Math.max(...this.templateItems.map((i) => i.no)) + 1;
    }
  }

  get asIds() {
    return this.templateItems.map((i) => String(i.no));
  }

  addRow() {
    if (this.templateItems.length >= ServiceConfig.LessonReport.maxItemCount) return;

    const newItem: LessonReportItemT = {
      no: this.nextNo++,
      index: this.templateItems.length,
      title: '',
      type: 'text',
      // 기본 옵션들 (원하면 조정 가능)
      selectOptions: [],
      textVariant: 'short',
    };
    this.templateItems.push(newItem);
  }

  removeRow(no: number) {
    this.templateItems = this.templateItems.filter((i) => i.no !== no);
    this.reindex();
  }

  updateField<K extends keyof LessonReportItemT>(no: number, key: K, value: LessonReportItemT[K]) {
    const idx = this.templateItems.findIndex((i) => i.no === no);
    if (idx === -1) return;
    const next = { ...this.templateItems[idx], [key]: value } as LessonReportItemT;
    this.templateItems.splice(idx, 1, next);
  }

  reorder(fromIndex: number, toIndex: number, animationDurationMs: number) {
    if (fromIndex === toIndex) return;
    const copy = [...this.templateItems];
    const [moved] = copy.splice(fromIndex, 1);
    copy.splice(toIndex, 0, moved);
    this.templateItems = copy;
    this.reindex();

    // ✅ 나타나는 애니메이션용 (이미 쓰고 있던 로직)
    this.lastMovedNo = moved.no;
    this.moveTick += 1;

    // ✅ 사라지는 위치: "어느 인덱스 다음에" dummy를 보여줄지
    // fromIndex 기준으로(혹은 min(fromIndex, toIndex) 등 필요에 따라 조정)
    this.disappearingRowAfterIndex = fromIndex;

    // 일정 시간 뒤에 없애고 싶다면 setTimeout 안에서 null로
    setTimeout(() => {
      // mobx strict 모드면 runInAction 써주는 게 안전
      this.disappearingRowAfterIndex = null;
      this.lastMovedNo = null;
    }, animationDurationMs); // Collapse timeout 보다 조금 길게
  }

  private reindex() {
    this.templateItems = this.templateItems.map((it, idx) => ({
      ...it,
      index: idx,
    }));
  }

  handleDrop = (from: number, rawTo: number, animationDurationMs: number) => {
    // rawTo는 "index = rawTo인 아이템 앞에 삽입" 의미.
    // 먼저 from을 제거한다고 가정하면 from < rawTo일 때 최종 인덱스가 1 줄어듦.
    const to = from < rawTo ? rawTo - 1 : rawTo;
    if (from !== to) this.reorder(from, to, animationDurationMs);
  };

  setItemType(no: number, type: LessonReportItemTypeT) {
    const idx = this.templateItems.findIndex((i) => i.no === no);
    if (idx === -1) return;

    const item = this.templateItems[idx];
    const patch: Partial<LessonReportItemT> = { type };

    if (type === 'select') {
      patch.selectOptions = item.selectOptions ?? [];
      patch.maxScore = undefined;
      patch.allowFractionalScore = undefined;
      patch.detailedGradeScale = undefined;
      patch.textVariant = undefined;
      // allowMissing는 그대로 두거나, 필요하면 여기서도 초기화
    } else if (type === 'grade') {
      patch.detailedGradeScale = item.detailedGradeScale ?? true;
      patch.selectOptions = undefined;
      patch.maxScore = undefined;
      patch.allowFractionalScore = undefined;
      patch.textVariant = undefined;
    } else if (type === 'score') {
      patch.maxScore = item.maxScore ?? 100;
      patch.allowFractionalScore = item.allowFractionalScore ?? false;
      patch.detailedGradeScale = undefined;
      patch.selectOptions = undefined;
      patch.textVariant = undefined;
    } else if (type === 'text') {
      patch.textVariant = item.textVariant ?? 'short';
      patch.maxScore = undefined;
      patch.allowFractionalScore = undefined;
      patch.detailedGradeScale = undefined;
      patch.selectOptions = undefined;
    } else {
      // boolean
      patch.maxScore = undefined;
      patch.allowFractionalScore = undefined;
      patch.detailedGradeScale = undefined;
      patch.selectOptions = undefined;
      patch.textVariant = undefined;
    }

    this.templateItems[idx] = { ...item, ...patch };
  }

  validateItem = (item: LessonReportItemT): ValidationResultT => {
    const {
      maxItemTitleLength,
      selectOptionsMinLength,
      selectOptionsMaxLength,
      selectOptionsMaxTextLength,
      maxScoreMinValue,
      maxScoreMaxValue,
    } = ServiceConfig.LessonReport;

    // 1. 공통: 제목 필수
    if (!item.title || item.title.trim().length === 0) {
      return { isValid: false, errorMessage: '항목명을 입력해 주세요.' };
    }

    if (item.title.trim().length > maxItemTitleLength) {
      return {
        isValid: false,
        errorMessage: `항목명은 ${maxItemTitleLength}글자 이내로 입력해 주세요.`,
      };
    }

    // 2. 타입별 검증
    if (item.type === 'select') {
      const options = item.selectOptions ?? [];

      // 2-1. 개수: 2개 이상 5개 이하
      if (options.length < selectOptionsMinLength || options.length > selectOptionsMaxLength) {
        return {
          isValid: false,
          errorMessage: `선택형 항목은 선택지를 ${selectOptionsMinLength}개 이상 ${selectOptionsMaxLength}개 이하로 설정해야 합니다.`,
        };
      }

      // 2-2. 각 항목 길이: 공백 포함 최대 5자, 빈 값 불가
      for (const opt of options) {
        const trimmed = opt?.trim() ?? '';
        if (trimmed.length === 0) {
          return { isValid: false, errorMessage: '선택형 항목에 빈 선택지는 사용할 수 없습니다.' };
        }
        if (trimmed.length > 5) {
          return {
            isValid: false,
            errorMessage: `선택형 항목의 각 선택지는 공백 포함 최대 ${selectOptionsMaxTextLength}자까지 입력할 수 있습니다.`,
          };
        }
      }
    } else if (item.type === 'score') {
      const maxScore = item.maxScore;

      // 3-1. 만점 값 존재 여부
      if (!maxScore) {
        return { isValid: false, errorMessage: '점수형 항목은 만점을 설정해야 합니다.' };
      }

      // 3-2. 숫자인지 체크
      if (typeof maxScore !== 'number' || Number.isNaN(maxScore)) {
        return { isValid: false, errorMessage: '점수형 항목의 만점은 숫자로 입력해야 합니다.' };
      }

      // 3-3. 범위: 1점 이상 1000점 이하
      if (maxScore < maxScoreMinValue || maxScore > maxScoreMaxValue) {
        return {
          isValid: false,
          errorMessage: `점수형 항목의 만점은 ${maxScoreMinValue}점 이상 ${maxScoreMaxValue}점 이하로 설정해야 합니다.`,
        };
      }

      // 3-4. 정수인지 (소수 허용 여부와 관계 없이)
      if (!Number.isInteger(maxScore)) {
        return {
          isValid: false,
          errorMessage: '점수형 항목의 만점은 소수점 없이 정수로 입력해야 합니다.',
        };
      }
    }

    // grade / text / boolean 등은 현재 별도 제약 없음
    return { isValid: true };
  };

  handleSubmit = async (values: LessonReportTemplateFormValuesT): Promise<void> => {
    if (this.templateItems.length === 0) {
      await interfaceBridge.customAlert(
        `수업 리포트 템플릿 저장 실패`,
        '하나 이상의 템플릿 항목을 추가해 주세요.',
        '닫기',
      );

      return;
    }

    for (const item of this.templateItems) {
      const validationResult = this.validateItem(item);
      if (!validationResult.isValid) {
        await interfaceBridge.customAlert(
          `수업 리포트 템플릿 저장 실패`,
          `${item.index + 1}번째 항목에 문제가 있습니다. ${validationResult.errorMessage}`,
          '닫기',
        );

        return;
      }
    }

    let hasLongComment = false;
    for (const item of [...this.templateItems].sort((a, b) => a.index - b.index)) {
      if (item.type === 'text' && item.textVariant === 'long') {
        hasLongComment = true;
      } else if (hasLongComment) {
        await interfaceBridge.customAlert(
          '수업 리포트 템플릿 저장 실패',
          '모든 장문 코멘트는 마지막 부분에 배치해 주세요.',
          '닫기',
        );
        return;
      }
    }

    const lessonReportTemplate: LessonReportTemplateT = {
      id: 0,
      academyId: 0,
      templateTitle: values.templateTitle,
      publicTitle: values.publicTitle,
      items: this.templateItems,
    };

    try {
      const result = await api<LessonReportTemplateT, MyResponse<number>>(
        CREATE_LESSON_REPORT_TEMPLATE(0),
        lessonReportTemplate,
      );

      // 내부작업
      if (result.isSuccess) {
        await interfaceBridge.customAlert(
          '수업 보고서 템플릿 저장 성공',
          '템플릿을 성공적으로 저장했습니다.',
          '닫기',
        );
        interfaceBridge.back('{ "edited": true }');
      } else {
        await interfaceBridge.customAlert(
          '수업 보고서 템플릿 저장 실패',
          `오류가 발생했습니다. (${result.errorCode})`,
          '닫기',
        );
      }
    } catch (e) {
      MyError.handleCatchedError(e);
    }
  };
}

export default LessonReportTemplateAddStore;
